import React, { createContext, useCallback, useContext, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Slide,
    Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
    ErrorOutline as ErrorIcon,
    CheckCircleOutline as SuccessIcon,
    InfoOutlined as InfoIcon,
    WarningAmberOutlined as WarningIcon,
    Close as CloseIcon,
} from '@mui/icons-material';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AlertSeverity = 'error' | 'warning' | 'info' | 'success';

export interface ConfirmOptions {
    title?: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    severity?: 'warning' | 'error' | 'info';
}

export interface AlertOptions {
    title?: string;
    message: string;
    severity?: AlertSeverity;
    okLabel?: string;
}

interface DialogContextValue {
    confirm: (options: ConfirmOptions) => Promise<boolean>;
    alert: (options: AlertOptions) => Promise<void>;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const DialogContext = createContext<DialogContextValue | null>(null);

// ─── Internal state types ─────────────────────────────────────────────────────

interface ConfirmState extends ConfirmOptions {
    open: boolean;
    resolve: (value: boolean) => void;
}

interface AlertState extends AlertOptions {
    open: boolean;
    resolve: () => void;
}

// ─── Transition ───────────────────────────────────────────────────────────────

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// ─── Severity helpers ─────────────────────────────────────────────────────────

const severityColor: Record<AlertSeverity, string> = {
    error: '#d32f2f',
    warning: '#ed6c02',
    info: '#0288d1',
    success: '#2e7d32',
};

const SeverityIcon: React.FC<{ severity: AlertSeverity; size?: number }> = ({ severity, size = 28 }) => {
    const sx = { fontSize: size, color: severityColor[severity] };
    switch (severity) {
        case 'error': return <ErrorIcon sx={sx} />;
        case 'warning': return <WarningIcon sx={sx} />;
        case 'success': return <SuccessIcon sx={sx} />;
        case 'info':
        default: return <InfoIcon sx={sx} />;
    }
};

// ─── Provider ────────────────────────────────────────────────────────────────

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
    const [alertState, setAlertState] = useState<AlertState | null>(null);

    const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            setConfirmState({ ...options, open: true, resolve });
        });
    }, []);

    const alert = useCallback((options: AlertOptions): Promise<void> => {
        return new Promise<void>((resolve) => {
            setAlertState({ ...options, open: true, resolve });
        });
    }, []);

    // ── Confirm handlers ──────────────────────────────────────────────────────

    const handleConfirm = () => {
        confirmState?.resolve(true);
        setConfirmState(null);
    };

    const handleCancel = () => {
        confirmState?.resolve(false);
        setConfirmState(null);
    };

    // ── Alert handler ─────────────────────────────────────────────────────────

    const handleAlertOk = () => {
        alertState?.resolve();
        setAlertState(null);
    };

    // ── Color for confirm button ──────────────────────────────────────────────

    const confirmBtnColor = (() => {
        switch (confirmState?.severity) {
            case 'error': return 'error' as const;
            case 'warning': return 'warning' as const;
            default: return 'primary' as const;
        }
    })();

    const alertSeverity: AlertSeverity = alertState?.severity ?? 'info';

    return (
        <DialogContext.Provider value={{ confirm, alert }}>
            {children}

            {/* ── Confirm Dialog ───────────────────────────────────────────── */}
            <Dialog
                open={Boolean(confirmState?.open)}
                TransitionComponent={Transition}
                onClose={handleCancel}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1, pr: 6 }}>
                    <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                        {confirmState?.title ?? 'Confirm'}
                    </Typography>
                    <IconButton
                        onClick={handleCancel}
                        size="small"
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <DialogContentText sx={{ color: 'text.primary' }}>
                        {confirmState?.message}
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                        sx={{ borderRadius: 2, minWidth: 90 }}
                    >
                        {confirmState?.cancelLabel ?? 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        color={confirmBtnColor}
                        onClick={handleConfirm}
                        sx={{ borderRadius: 2, minWidth: 90 }}
                        autoFocus
                    >
                        {confirmState?.confirmLabel ?? 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Alert / Notification Dialog ──────────────────────────────── */}
            <Dialog
                open={Boolean(alertState?.open)}
                TransitionComponent={Transition}
                onClose={handleAlertOk}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
                    <SeverityIcon severity={alertSeverity} />
                    <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                        {alertState?.title ?? alertSeverity.charAt(0).toUpperCase() + alertSeverity.slice(1)}
                    </Typography>
                    <IconButton
                        onClick={handleAlertOk}
                        size="small"
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <DialogContentText sx={{ color: 'text.primary' }}>
                        {alertState?.message}
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleAlertOk}
                        sx={{
                            borderRadius: 2,
                            minWidth: 90,
                            backgroundColor: severityColor[alertSeverity],
                            '&:hover': {
                                backgroundColor: severityColor[alertSeverity],
                                filter: 'brightness(0.9)',
                            },
                        }}
                        autoFocus
                    >
                        {alertState?.okLabel ?? 'OK'}
                    </Button>
                </DialogActions>
            </Dialog>
        </DialogContext.Provider>
    );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useDialog = (): DialogContextValue => {
    const ctx = useContext(DialogContext);
    if (!ctx) {
        throw new Error('useDialog must be used inside <DialogProvider>');
    }
    return ctx;
};
